import { apiClient } from './api';

export interface MealLogEntry {
  id: number;
  meal_name: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fats_g: number;
  meal_type: string | null;
  logged_at: string;
  created_at?: string;
}

export interface DailySummary {
  date: string;
  totals: {
    calories: number;
    protein_g: number;
    carbs_g: number;
    fats_g: number;
  };
  by_meal_type: Array<{
    meal_type: string;
    calories: number;
    protein_g: number;
    carbs_g: number;
    fats_g: number;
    count: number;
  }>;
  meals: MealLogEntry[];
}

export interface HistoryEntry {
  date: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fats_g: number;
  meal_count: number;
}

export interface LogMealInput {
  meal_name: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fats_g: number;
  meal_type?: string;
  logged_at?: string;
}

export interface AiEstimateResult {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fats_g: number;
  description?: string;
}

export interface NutritionistProfileData {
  id?: number;
  isActive: boolean;
  dailyCalorieTarget: number | null;
  goalType: string;
  activityLevel: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgentInfo {
  key: string;
  displayName: string;
  tagline: string;
  avatarUrl: string | null;
  isActive: boolean;
}

export interface AgentsTeamResponse {
  agents: AgentInfo[];
}

export class NutritionService {
  static async logMeal(input: LogMealInput): Promise<MealLogEntry> {
    const response = await apiClient.post<MealLogEntry>('/api/nutrition/log-meal', input);
    return response.data;
  }

  static async deleteMeal(id: number): Promise<void> {
    await apiClient.delete(`/api/nutrition/meal/${id}`);
  }

  static async getDailySummary(date?: string): Promise<DailySummary> {
    const params = date ? `?date=${date}` : '';
    const response = await apiClient.get<DailySummary>(`/api/nutrition/daily-summary${params}`);
    return response.data;
  }

  static async getHistory(days: number = 7): Promise<HistoryEntry[]> {
    const response = await apiClient.get<HistoryEntry[]>(`/api/nutrition/history?days=${days}`);
    return response.data;
  }

  static async estimateCalories(mealDescription: string): Promise<AiEstimateResult> {
    const response = await apiClient.post<AiEstimateResult>('/api/ai/estimate-calories', {
      description: mealDescription,
    });
    return response.data;
  }

  static async getNutritionistProfile(): Promise<NutritionistProfileData> {
    const response = await apiClient.get<NutritionistProfileData>('/api/nutritionist-profile');
    return response.data;
  }

  static async updateNutritionistProfile(data: Partial<NutritionistProfileData>): Promise<NutritionistProfileData> {
    const response = await apiClient.put<NutritionistProfileData>('/api/nutritionist-profile', data);
    return response.data;
  }

  static async getAgentsTeam(): Promise<AgentsTeamResponse> {
    const response = await apiClient.get<AgentsTeamResponse>('/api/agents/team');
    return response.data;
  }
}
